import { localized, useTranslation } from "@msuite/ui/localization";
import type { PersonioSyncRow } from "./utils";

/** Props Interface */
interface PersonioSyncNoteCellProps {
	row: PersonioSyncRow;
}

export function PersonioSyncNoteCell({ row }: PersonioSyncNoteCellProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Functions */
	// What the last sync has to say about the row outranks what the plan said about it: a failed
	// row keeps its planned state, and the reason it failed is what the office needs next.
	function getResultNote() {
		if (!row.result) return null;
		const { outcome, issue, messageKey, messageParams, message, isEmailSent } = row.result;
		if (outcome === "created" && !isEmailSent) {
			return t("integrations.personio_sync.email_not_sent");
		}
		if (outcome === "adopted") return t("integrations.personio_sync.adopted_hint");
		if (outcome !== "failed" || !issue) return null;
		const reason = t(`integrations.personio_sync.issues.${issue}`);
		const detail = messageKey ? localized(messageKey, messageParams ?? undefined) : message;
		return detail ? `${reason}: ${detail}` : reason;
	}

	function getPlanNote() {
		if (row.action === "link" && row.isOverridden) {
			return t("integrations.personio_sync.chosen_by_hand");
		}
		if (row.action === "link" && row.matchedBy === "email") {
			return t("integrations.personio_sync.matched_by_email");
		}
		if (row.action === "link" && row.matchedBy === "name") {
			return t("integrations.personio_sync.matched_by_name");
		}
		if (row.action === "create") return t("integrations.personio_sync.new_account");
		if (row.action === "adopt") return t("integrations.personio_sync.existing_account");
		if (!row.action && row.issue) return t(`integrations.personio_sync.issues.${row.issue}`);
		return null;
	}

	/** Computed */
	const note = getResultNote() ?? getPlanNote();

	/** Guard */
	if (!note) return null;

	/** Render */
	return (
		<span
			className="truncate"
			title={note}
		>
			{note}
		</span>
	);
}
